import {
  forwardRef,
  type CSSProperties,
  type HTMLAttributes,
  type ReactNode,
} from "react";

export type ScreenFrameTheme = "sunset" | "dawn";
export type ScreenFrameForm = "ring" | "disc";

export interface ScreenFrameProps extends HTMLAttributes<HTMLDivElement> {
  /** State of the light; sets `data-theme` on the frame. @default 'sunset' */
  theme?: ScreenFrameTheme;
  /** The sky wash, `--d-sky`. @default true */
  sky?: boolean;
  /**
   * The sun glow at `--d-sun-y`. Turn it off on a screen that draws its own
   * glow inside its scroller (Today does — otherwise the light doubles).
   * @default true
   */
  glow?: boolean;
  /** The grain. @default true */
  grain?: boolean;
  /** `data-form` — the CSS mask shape of a dot-work stage. */
  form?: ScreenFrameForm;
  /** `data-body` — the key of the day's body. */
  body?: string;
  children?: ReactNode;
}

/**
 * The grain: an inline SVG `feTurbulence` tile (fractalNoise 0.85, 2
 * octaves, 256px tile, rect opacity .55). Copied verbatim from
 * `docs/design/handoff/design/components/chrome/ScreenFrame.jsx` — the exact
 * frequency is what makes the dot-work read as one surface, so do not
 * "clean it up".
 *
 * Exported so a test can assert the tile: jsdom's CSSOM drops a data-URI
 * `background-image`, so it cannot be read back off the rendered element.
 */
export const SCREEN_FRAME_GRAIN = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='256' height='256'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/%3E%3C/filter%3E%3Crect width='256' height='256' filter='url(%23n)' opacity='0.55'/%3E%3C/svg%3E")`;

/* The frame is a full-viewport background, not the handoff's 390x844
 * artboard: no border, no 28px radius, full width, and its height comes
 * from `--app-vh` — the same variable `#root` uses (index.css), published
 * by the Telegram bridge from `viewportStableHeight`. Everything inside
 * the layers below is copied verbatim from the handoff. */
const frameStyle: CSSProperties = {
  position: "relative",
  width: "100%",
  height: "var(--app-vh, 100dvh)",
  /* `viewport-fit=cover` (index.html) puts the page under the iOS status bar
   * and the Telegram header, so without this the first ~47px of every screen
   * sit under the clock. v1 solved it per-screen with `IosStatusBarScrim`;
   * the frame owns it once instead, which is why the screens built on it had
   * simply lost the inset. Tailwind's preflight makes this border-box, so the
   * padding comes out of `--app-vh` rather than adding to it. */
  paddingTop: "var(--d-screen-pad-top, env(safe-area-inset-top))",
  overflow: "hidden",
  background: "var(--d-ground)",
  flex: "none",
};

/* Sky, glow and grain are `position: fixed`, not `absolute` as in the
 * handoff. The prototype's frame is an artboard that never scrolls, so
 * `absolute` layers stay put inside it. The app instead has a single
 * scroll container (`.app-scroll` — index.css, MAV-110): an absolutely
 * positioned layer would be laid out against the frame's box and scroll
 * away with the content, leaving the grain covering only the first
 * viewport and the glow sliding off the top. `fixed` pins all three to the
 * viewport, which is what the artboard's `absolute` meant visually.
 *
 * They are not clipped by the frame's `overflow: hidden`: a fixed element
 * escapes an ancestor's clip unless that ancestor establishes a containing
 * block (transform / filter / contain), which the frame does not. And
 * `html, body { overflow: hidden }` keeps the 780px glow box from ever
 * producing a scrollbar. */
const skyStyle: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "var(--d-sky)",
  pointerEvents: "none",
};

const glowStyle: CSSProperties = {
  position: "fixed",
  left: "50%",
  top: "var(--d-sun-y)",
  width: 780,
  height: 780,
  background:
    "radial-gradient(circle closest-side,var(--d-sun) 0%,var(--d-sun) 40%,transparent 94%)",
  transform: "translate(-50%,-50%)",
  pointerEvents: "none",
};

const grainStyle: CSSProperties = {
  position: "fixed",
  inset: 0,
  pointerEvents: "none",
  mixBlendMode: "overlay",
  opacity: "var(--d-grain-o)",
  zIndex: 30,
  backgroundImage: SCREEN_FRAME_GRAIN,
};

/**
 * The single backdrop of every PSYNA screen: `--d-ground`, the sky
 * (`--d-sky`), the sun glow (`--d-sun` at `--d-sun-y`) and the grain over
 * everything. Never assemble those three by hand — see
 * `docs/design/handoff/05-SHARED.md` §"Каркас экрана".
 *
 * Layers, bottom to top: sky → glow → `children` → grain. The grain sits at
 * `z-index: 30`; a tab bar belongs at 20, i.e. under the grain, which is
 * deliberate.
 *
 * Ported from `docs/design/handoff/design/components/chrome/ScreenFrame.jsx`.
 * Two deliberate differences, both from the E0 porting decisions: the frame
 * is a full-viewport background rather than a 390×844 artboard (see
 * `frameStyle`), and the three layers are `fixed` rather than `absolute`
 * (see the comment above `skyStyle`). Every value inside the layers is
 * verbatim.
 *
 * Nothing here animates, so there is no `prefers-reduced-motion` branch.
 */
export const ScreenFrame = forwardRef<HTMLDivElement, ScreenFrameProps>(
  function ScreenFrame(
    {
      theme = "sunset",
      sky = true,
      glow = true,
      grain = true,
      form,
      body,
      className,
      style,
      children,
      ...rest
    },
    ref,
  ) {
    return (
      <div
        ref={ref}
        className={className}
        data-theme={theme}
        data-form={form}
        data-body={body}
        style={{ ...frameStyle, ...style }}
        {...rest}
      >
        {sky ? <div data-screen-frame-layer="sky" style={skyStyle} /> : null}
        {glow ? <div data-screen-frame-layer="glow" style={glowStyle} /> : null}
        {children}
        {grain ? (
          <div data-screen-frame-layer="grain" style={grainStyle} />
        ) : null}
      </div>
    );
  },
);

export default ScreenFrame;
